import { Box } from "@chakra-ui/react";
import { colors, ColorIcon, customEvent, Field, GoiLogo, Spacing } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { PrevNext } from "../RequestQuestionUniqueStep/RequestQuestionStepByStep";
import { MubinsoCtaButton, MubinsoKeepAll } from "./MubinsoUI";
import MubinsoMainFunnel from "./MubinsoMainFunnel";
import MubinsoProcedureFunnelB from "./MubinsoProcedureFunnelB";
import MubinsoProcedureFunnelE4 from "./MubinsoProcedureFunnelE4";
import MubinsoCostFunnelA from "./MubinsoCostFunnelA";
import MubinsoCostFunnelE4 from "./MubinsoCostFunnelE4";
import MubinsoParlorFindFunnel from "./MubinsoParlorFindFunnel";
import MubinsoParlorFindFunnelE4 from "./MubinsoParlorFindFunnelE4";
import MubinsoLandingB from "./MubinsoLandingB";
import MubinsoLandingE4 from "./MubinsoLandingE4";
import { MubinsoLeadModal } from "./MubinsoLeadModal";
import { buildMubinsoLeadMessage } from "./mubinsoLeadMessage";
import { MUBINSO_E4_URGENT_MODAL, MUBINSO_E4_URGENT_TOPIC } from "./mubinsoE4Constants";
import type { ParlorOption } from "@/types/parlorOption";
import { getVariationOnDemand, HackleClient } from "@/utils/hackle";
import { isFamilyAdGroup } from "@/utils/mubinsoFamilyAdGroup";
import {
  MUBINSO_PARLOR_PICK_EXPERIMENT_KEY,
  shouldMountMubinsoParlorPickExperiment,
} from "@/utils/mubinsoParlorPickExperiment";
import { markMubinsoLandingB, isMubinsoLandingBTarget } from "@/utils/mubinsoLandingBContext";
import { MUBINSO_PROCEDURE_E4_EXPERIMENT_KEY } from "@/utils/mubinsoProcedureE4Experiment";
import MubinsoGuideScreen from "./MubinsoGuideScreen";
import { MubinsoFamilyIntroScreen } from "./MubinsoFamilyScreens";
import {
  FAMILY_BRANCH_OPTIONS,
  FamilyBranchKey,
  familyStepScreen,
  familyTopic,
  isFamilyFunnel,
  isUrgentSituation,
  MUBINSO_LANDING,
  MUBINSO_SITUATION_OPTIONS,
  MUBINSO_SITUATION_TITLE,
  MUBINSO_TOPIC_OPTIONS,
  MUBINSO_TOPIC_TITLE,
  MubinsoEntry,
  mubinsoEntryTrigger,
  MubinsoFunnelType,
  MubinsoScreen,
  resolveFunnel,
  situationOptionByLabel,
  topicOptionByLabel,
} from "./mubinsoConstants";

/**
 * 무빈소 신규 리드 퍼널. 라우트 없이 내부 스크린 상태머신으로만 전환.
 *
 * 흐름: 랜딩 → 주제(첫질문) → 상황(둘째질문) → 퍼널(메인/절차/비용/찾기) → 가이드
 * 분기: 상황이 임종/임박(urgent) → 주제 무관 메인. 그 외는 주제(topic)대로.
 *
 * M5 가족장(가족장 소재 n_ad 또는 ?template=family): 랜딩 대신 소개(분기 카드 포함). 빈소 있음이면 일반 견적,
 * 빈소 없음이면 Q1 · Q2 · 퍼널을 위와 똑같이 탄다.
 * Hackle 실험에는 배정하지 않고, 개별 이벤트는 fam 으로 묶는다. 명세: wiki/plans/mubinso-family-funnel.md
 */
/**
 * @param parlorOptions 관심 장례식장 체크 화면용 전국 목록(찾기 퍼널 전용).
 *   다른 퍼널은 쓰지 않아 baseProps 가 아니라 찾기 퍼널에만 넘긴다.
 */
export default function MubinsoFunnelContainer({
  parlorOptions = [],
}: {
  parlorOptions?: ParlorOption[];
} = {}) {
  const router = useRouter();
  // 부모(MatchRequestContainer)가 router.isReady 이후에만 렌더하므로 첫 렌더에 query 가 있다
  const isFamily = isFamilyFunnel(router.query.template, router.query.n_ad, router.query.fam_exit);
  const [screen, setScreen] = useState<MubinsoScreen>(
    isFamily ? familyStepScreen(router.query.fam_step) : "landing",
  );
  const [topicAnswer, setTopicAnswer] = useState("");
  const [situationAnswer, setSituationAnswer] = useState("");
  const [funnelType, setFunnelType] = useState<MubinsoFunnelType>("main");
  /** Q2 응답 시점에 확정된다. 그 전에는 A(현행)로 흐른다 */
  const [isPickVariant, setIsPickVariant] = useState(false);
  /**
   * E4 개편 배정(utils/mubinsoProcedureE4Experiment.ts). E4 는 첫 화면부터 달라 첫 화면 진입 때 정한다.
   * null 은 배정 중이다. 그동안 첫 화면을 그리지 않아 현행 → E4 로 바뀌는 깜빡임을 막는다.
   * 키가 0 이거나 첫 화면 B 실험(n_ad) 대상이면 배정하지 않고 현행으로 흐른다(두 실험을 섞지 않는다).
   */
  const [isE4, setIsE4] = useState<boolean | null>(null);
  const [isE4UrgentOpen, setIsE4UrgentOpen] = useState(false);
  const [familyBranch, setFamilyBranch] = useState<FamilyBranchKey | null>(null);

  // n_ad 세션 마킹 (새로고침/카카오 리다이렉트 시 쿼리 유실 대비)
  useEffect(() => {
    markMubinsoLandingB(router.query.n_ad);
  }, [router.query.n_ad]);

  const showLandingB = isMubinsoLandingBTarget(router.query.n_ad);
  // M5 가족장 광고그룹(A·B 소재 모두)과 가족장 QA 링크는 배정하지 않고 현행으로 둔다(PR #2728).
  // 가족장 비교의 A 소재가 현행이어야 해서 소재가 아니라 광고그룹 단위로 뺀다.
  // 가족장 화면(isFamily, n_ad 로만 판정되는 경우 포함)도 함께 뺀다.
  const isFamilyEntry =
    isFamilyAdGroup(router.query.n_ad_group) || router.query.template === "family";

  useEffect(() => {
    if (isE4 !== null) return;
    if (MUBINSO_PROCEDURE_E4_EXPERIMENT_KEY <= 0 || showLandingB || isFamily || isFamilyEntry) {
      setIsE4(false);
      return;
    }
    // HackleProvider 가 supportSSR 라 SDK 준비 전에 이 화면이 그려진다. 준비 전에 물으면 기본값 A 가 나와
    // 배정이 A 로 쏠리므로 준비를 기다린다(Q2 응답 때 묻는 #352 는 이미 준비된 뒤라 괜찮았다).
    // 제한 시간은 HackleProvider 와 같은 3초. 넘으면 그때 값으로 정한다
    let cancelled = false;
    HackleClient.onInitialized({ timeout: 3000 })
      .catch(() => undefined)
      .then(() => {
        if (cancelled) return;
        // getVariationOnDemand 는 GA4 노출을 쏘지 않아 직접 쏜다(아래 #352 와 같은 이유). Z 는 미할당
        const variation = getVariationOnDemand(MUBINSO_PROCEDURE_E4_EXPERIMENT_KEY);
        setIsE4(variation === "B");
        if (variation !== "Z") {
          customEvent({
            newGtm: {
              action: "experiment_exposure",
              properties: {
                experiment_variation: `${MUBINSO_PROCEDURE_E4_EXPERIMENT_KEY}-${variation}`,
              },
            },
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isE4, showLandingB, isFamily, isFamilyEntry]);

  /*
   * 가족장은 소개 → Q1 으로 넘어갈 때 브라우저 기록을 쌓는다(fam_step 쿼리, shallow).
   * 모바일에서 무심코 누른 뒤로 가기가 페이지 이탈이 아니라 앞 화면으로 돌아오게 한다(개발팀 제안).
   * 화면은 쿼리를 따라간다. Q2 · 퍼널은 A 와 같은 화면이라 기록을 쌓지 않아, 거기서 뒤로 가면 소개로 온다.
   * 같은 페이지 안 이동이라 이탈 방지 모달(useRouterChangeLoad)은 뜨지 않는다.
   */
  const familyStep = router.query.fam_step;
  useEffect(() => {
    if (!isFamily) return;
    setScreen(familyStepScreen(familyStep));
    // 소개로 돌아오면 다시 고를 수 있게 연타 방지 값을 푼다
    setFamilyBranch(null);
  }, [isFamily, familyStep]);

  const pushFamilyTopic = () =>
    router.push({ query: { ...router.query, fam_step: "topic" } }, undefined, { shallow: true });

  // 카드를 누르고 180ms 뒤 이동한다. 그 사이 뒤로 가기 등으로 화면을 벗어나면 이동하지 않게 지운다
  const familyMoveTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(familyMoveTimer.current), [familyStep]);

  // 화면 전환 시 스크롤 최상단으로(이전 화면에서 스크롤 내려둔 상태로 다음 화면이 잘리는 것 방지).
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [screen]);

  const isUrgent = isUrgentSituation(situationAnswer);

  /*
   * 관심 장례식장 체크 A/B 모수는 Q1 에서 "장례식장을 찾고싶어요" 를 고른 사람뿐이다.
   * Q2 화면은 모든 주제가 지나가므로 여기서 걸러야 다른 주제 진입자가 분모에 들어오지 않는다.
   * 가족장은 A/B 를 광고 소재로 나누는 별도 실험이라 배정하지 않는다(현행 A 로 흐른다).
   * 비교군인 A 소재도 현행을 봐야 하므로 가족장 화면 여부가 아니라 M5 광고그룹 단위로 뺀다.
   */
  const isPickExperimentTarget =
    !isFamily &&
    !isFamilyAdGroup(router.query.n_ad_group) &&
    topicOptionByLabel(topicAnswer)?.funnel === "parlorFind" &&
    shouldMountMubinsoParlorPickExperiment(parlorOptions.length > 0);

  /** Q1 답을 확정하고 다음 화면으로. E4 첫 화면 버튼(label 을 바로 받음)과 Q1 화면이 같이 쓴다 */
  const goFromTopic = (label: string) => {
    // 확정 답변 기준으로 응답 이벤트 발화(탭마다 발화 시 중복 집계 → 비율 왜곡 방지).
    const ev = topicOptionByLabel(label)?.event;
    if (ev) customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: ev } });
    customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: "q1-next" } });

    // E4 는 Q2 없이 바로 E4 퍼널(절차 · 비용 · 장례식장)로 간다(상황은 결과 모달에서 받는다).
    // 장례식장은 Q2 응답 때 배정하는 관심 장례식장 체크 실험을 거치지 않는다(E4 와 섞지 않는다)
    const topicFunnel = topicOptionByLabel(label)?.funnel;
    if (isE4 && topicFunnel) {
      setSituationAnswer("");
      setFunnelType(topicFunnel);
      customEvent({
        newGtm: { action: "mubinso_funnel_enter", properties: { funnel: topicFunnel } },
      });
      setScreen("funnel");
      return;
    }
    setScreen("situation");
  };

  // E4 는 첫 화면이 Q1 을 겸해 「topic」 화면이 없다. 뒤로 가면 첫 화면으로
  const topicScreen: MubinsoScreen = isE4 ? "landing" : "topic";

  if (screen === "familyIntro") {
    return (
      <MubinsoFamilyIntroScreen
        value={familyBranch}
        onPick={(key) => {
          // 이동 대기(180ms) 중 다시 눌러도 이벤트·이동이 두 번 일어나지 않게 한다
          if (familyBranch) return;
          const option = FAMILY_BRANCH_OPTIONS.find((o) => o.key === key);
          if (option) {
            customEvent({
              newGtm: { path: "mubinso_funnel", trigger_and_target: option.event },
            });
          }
          setFamilyBranch(key);
          // 선택 표시를 잠깐 보여 준 뒤 이동한다(시안 180ms)
          familyMoveTimer.current = setTimeout(() => {
            if (key === "binso") {
              // 빈소 있음은 무빈소 퍼널을 벗어나 일반 견적으로 간다. 같은 페이지라 이탈 방지 모달은 뜨지 않는다.
              // 광고 쿼리(n_query, n_ad 등)는 남겨 일반 견적이 광고 유입으로 알아보게 하고,
              // fam_exit=binso 로 가족장 · 무빈소 판정만 끈다(isFamilyBinsoExit). template · fam_step 은 뺀다.
              const adQuery = { ...router.query };
              delete adQuery.template;
              delete adQuery.fam_step;
              router.push({
                pathname: "/match/request/",
                query: { ...adQuery, fam_exit: "binso" },
              });
              return;
            }
            pushFamilyTopic();
          }, 180);
        }}
      />
    );
  }

  if (screen === "landing") {
    const handleLandingNext = () => {
      customEvent({
        newGtm: {
          path: "mubinso_funnel",
          trigger_and_target: showLandingB ? "landingB-cta" : "landing-cta",
        },
      });
      setScreen("topic");
    };
    if (isE4 === null) return null;
    if (isE4)
      return (
        <>
          <MubinsoLandingE4
            onPickTopic={(label) => {
              // 첫 화면 → 다음 비율을 현행과 같은 축으로 보려고 landing-cta 도 함께 발화한다
              customEvent({
                newGtm: { path: "mubinso_funnel", trigger_and_target: "landing-cta" },
              });
              setTopicAnswer(label);
              goFromTopic(label);
            }}
            onUrgent={() => {
              customEvent({
                newGtm: { path: "mubinso_funnel", trigger_and_target: "landing-urgent" },
              });
              // 신청 창을 바로 여는 버튼이라 A 의 결과 화면 버튼과 같은 이름도 쏜다(판정 지표 「신청 창 연 비율」)
              customEvent({
                newGtm: { path: "mubinso_funnel", trigger_and_target: "conv-info_cta" },
              });
              customEvent({
                newGtm: {
                  path: "mubinso_funnel",
                  trigger_and_target: mubinsoEntryTrigger("urgent", "cta_main"),
                },
              });
              setIsE4UrgentOpen(true);
            }}
          />
          {/* E4 는 Q2 가 없어 급한 고객을 이 버튼으로 받는다. 번호만 받고 바로 전화하므로 임종상태로 보낸다 */}
          <MubinsoLeadModal
            isOpen={isE4UrgentOpen}
            onClose={() => setIsE4UrgentOpen(false)}
            title={MUBINSO_E4_URGENT_MODAL.title}
            description={<MubinsoKeepAll>{MUBINSO_E4_URGENT_MODAL.description}</MubinsoKeepAll>}
            disableUntilFilled
            wide
            buttonText={MUBINSO_E4_URGENT_MODAL.buttonText}
            messageBuilder={() =>
              buildMubinsoLeadMessage({
                ctaType: "consult",
                situation: "",
                topic: MUBINSO_E4_URGENT_TOPIC,
              })
            }
            leadStatus="임종상태"
            // A 의 임종·임박 진입(Q2)과 같은 축(urg-lead_try / urg-lead_ok)으로 센다
            entry="urgent"
            // 신청 후에도 첫 화면에 남는다(가이드 화면으로 보내지 않는다)
            onSubmitted={() => setIsE4UrgentOpen(false)}
          />
        </>
      );
    return showLandingB ? (
      <MubinsoLandingB onNext={handleLandingNext} />
    ) : (
      <MubinsoLandingScreen onNext={handleLandingNext} />
    );
  }

  // 첫질문 - 관심 주제
  if (screen === "topic") {
    return (
      <MubinsoQuestionScreen
        title={MUBINSO_TOPIC_TITLE}
        options={MUBINSO_TOPIC_OPTIONS.map((o) => o.label)}
        value={topicAnswer}
        onChange={setTopicAnswer}
        onNext={() => goFromTopic(topicAnswer)}
        // 첫 질문이라 이전 없음(disabled 노출)
        onPrev={() => {}}
        prevDisabled
      />
    );
  }

  // 둘째질문 - 지금 상황(긴급도)
  if (screen === "situation") {
    return (
      <MubinsoQuestionScreen
        title={MUBINSO_SITUATION_TITLE}
        options={MUBINSO_SITUATION_OPTIONS.map((o) => o.label)}
        value={situationAnswer}
        onChange={setSituationAnswer}
        onNext={() => {
          // 확정 답변 기준으로 응답 이벤트 발화(탭마다 발화 시 중복 집계 → 비율 왜곡 방지).
          const ev = situationOptionByLabel(situationAnswer)?.event;
          if (ev) customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: ev } });
          customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: "q2-next" } });

          /*
           * 관심 장례식장 체크 A/B 배정.
           *
           * 왜 하필 여기인가
           * - A 와 B 가 갈리는 가장 이른 지점이 이 순간이다. 임박/임종은 여기서 A 면 메인
           *   퍼널로, B 면 찾기 퍼널로 갈라진다. 더 늦게 배정하면 A 가 현행이 될 수 없다.
           * - 노출이 찍히는 시점이 곧 분모다. 화면 진입이 아니라 **응답** 시점이라 Q2 를 보고
           *   이탈한 사람은 분모에 들어오지 않는다.
           *
           * 왜 훅이 아니라 getVariationOnDemand 인가
           * - 동기 함수라 이 자리에서 값을 바로 받아 쓴다. 훅으로 받으면 렌더 사이클을
           *   기다려야 해서 배정 전용 컴포넌트를 따로 띄워야 했다(그 방식도 A→B 깜빡임 없이
           *   만들려면 state 를 하나 더 두어야 한다).
           * - HackleProvider·HackleClient.setUser 와 같은 인스턴스를 쓴다. goi_common 의
           *   동명 함수는 별개 인스턴스라 유저 컨텍스트가 어긋난다(utils/hackle.ts 주석).
           *
           * ⚠️ 이 함수는 GA4 experiment_exposure 를 발화하지 않는다.
           *    useCustomVariation 이 하던 일이라 여기서 직접 쏜다.
           */
          let pickVariant = false;
          if (isPickExperimentTarget) {
            const variation = getVariationOnDemand(MUBINSO_PARLOR_PICK_EXPERIMENT_KEY);
            pickVariant = variation === "B";
            setIsPickVariant(pickVariant);
            // Z 는 미할당이다. useCustomVariation 도 Z 면 노출을 찍지 않는다
            if (variation !== "Z") {
              customEvent({
                newGtm: {
                  action: "experiment_exposure",
                  properties: {
                    experiment_variation: `${MUBINSO_PARLOR_PICK_EXPERIMENT_KEY}-${variation}`,
                  },
                },
              });
            }
          }

          // 긴급(임종/임박)은 주제 무관 메인 퍼널. 단 장례식장 찾기 + B안은 찾기 퍼널로 간다.
          // setState 는 비동기라 이번 판정에는 지역 변수를 쓴다
          const resolved = resolveFunnel(topicAnswer, situationAnswer, pickVariant);
          setFunnelType(resolved);
          customEvent({
            newGtm: { action: "mubinso_funnel_enter", properties: { funnel: resolved } },
          });
          setScreen("funnel");
        }}
        onPrev={() => {
          customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: "q2-prev" } });
          setScreen(topicScreen);
        }}
      />
    );
  }

  if (screen === "funnel") {
    const onComplete = () => setScreen("guide");

    const baseProps = {
      isUrgent,
      situationAnswer,
      topicAnswer: isFamily ? familyTopic(topicAnswer) : topicAnswer,
      onComplete,
    };

    // 찾기 퍼널은 entry 를 퍼널 안에서 topic_parlor(fnd)로 고정한다(Q1 장례식장 진입만 온다)
    if (isE4 && funnelType === "parlorFind")
      return (
        <MubinsoParlorFindFunnelE4
          topicAnswer={topicAnswer}
          onComplete={onComplete}
          onBack={() => setScreen(topicScreen)}
        />
      );
    if (funnelType === "parlorFind")
      return (
        <MubinsoParlorFindFunnel
          {...baseProps}
          parlorOptions={parlorOptions}
          isPickVariant={isPickVariant}
          family={isFamily}
        />
      );

    // 진입 구분. 렌더된 퍼널이 아니라 **진입** 기준이라야 개편 전후 지표가 같은 축에 선다.
    // funnelType === "main" && !isUrgent 가 "세가지 다" 진입이다(resolveFunnel 참고).
    // 가족장은 A 의 진입별 지표에 섞이지 않게 진입과 상관없이 family 하나로 묶는다.
    const entry: MubinsoEntry = isFamily
      ? "family"
      : isUrgent
      ? "urgent"
      : funnelType === "cost"
      ? "topic_cost"
      : funnelType === "main"
      ? "topic_all"
      : "topic_procedure";

    const funnelProps = { ...baseProps, entry };

    // 실험 330 종료(A 승) 후 확정 배치. 절차 개편만 B 를 남긴다.
    //   임종·임박  : A 메인 퍼널
    //   비용       : A 비용 퍼널
    //   절차       : B 절차 개편
    //   세가지 다  : B 절차 개편  ← 두 진입 병합은 B 의 처치였고 함께 채택
    // E4 배정자는 Q2 를 건너뛰고 E4 절차 · 비용 퍼널을 본다(isUrgent 는 항상 false).
    if (isUrgent) return <MubinsoMainFunnel {...funnelProps} family={isFamily} />;
    if (isE4 && funnelType === "cost")
      return (
        <MubinsoCostFunnelE4
          topicAnswer={topicAnswer}
          onComplete={onComplete}
          onBack={() => setScreen(topicScreen)}
          entry={entry}
        />
      );
    if (funnelType === "cost") return <MubinsoCostFunnelA {...funnelProps} family={isFamily} />;
    if (isE4 && (funnelType === "procedure" || funnelType === "main"))
      return (
        <MubinsoProcedureFunnelE4
          topicAnswer={topicAnswer}
          onComplete={onComplete}
          onBack={() => setScreen(topicScreen)}
          entry={entry}
        />
      );
    return <MubinsoProcedureFunnelB {...funnelProps} family={isFamily} />;
  }

  // guide - 리드 제출 완료 후 최종 화면
  return <MubinsoGuideScreen />;
}

// ---------------------------------------------------------------------------
// 랜딩(무빈소 장례 안내)
// ---------------------------------------------------------------------------
function MubinsoLandingScreen({ onNext }: { onNext: () => void }) {
  return (
    <BaseStack width="100%" maxW="480px" m="0 auto" minH="100dvh" px={24}>
      <Spacing gap={56} />
      <Box width="100%" whiteSpace="pre-line" className="head2_b black">
        {MUBINSO_LANDING.headline}
      </Box>
      <Spacing gap={12} />
      <Box width="100%" whiteSpace="pre-line" className="body_16_r gray600">
        {MUBINSO_LANDING.sub}
      </Box>

      <Spacing gap={32} />
      <BaseStack width="100%" bg={colors.warmGray100} borderRadius="16px" p={20} spacing={16}>
        <Box className="subtitle_16_b gray900">
          이런 내용을 <span style={{ color: colors.orange650 }}>무료로</span> 알려드려요
        </Box>
        <BaseStack width="100%" spacing={12}>
          {MUBINSO_LANDING.cardItems.map((item) => (
            <BaseStack key={item} direction="horizontal" spacing={10} alignItems="center">
              <Box
                flex="none"
                width="22px"
                height="22px"
                borderRadius="50%"
                bg={colors.orange200}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <ColorIcon name="icon-check-mono" width={12} height={12} color={colors.orange650} />
              </Box>
              <Box className="body_16_r gray800">{item}</Box>
            </BaseStack>
          ))}
        </BaseStack>
      </BaseStack>

      <Spacing gap={20} />
      <BaseStack width="100%" direction="horizontal" spacing={8} alignItems="flex-start">
        <Box flex="none" mt="2px">
          <GoiLogo color={colors.goiBlack} size={16} />
        </Box>
        <Box className="body_14_r gray700" whiteSpace="pre-line">
          장례 문화를 바꿔가고 있는 <b style={{ color: colors.gray800 }}>고이장례연구소</b>가 무빈소
          장례 1,000건 이상을 진행하며 정리한 내용이에요.
        </Box>
      </BaseStack>

      <Spacing gap={40} />
      <Box width="100%">
        <MubinsoCtaButton label={MUBINSO_LANDING.cta} onClick={onNext} />
      </Box>
      <Spacing gap={40} />
    </BaseStack>
  );
}

// ---------------------------------------------------------------------------
// 질문 화면(Q1/Q2 공용) - 기존 match/request 첫질문 UI와 동일한 레이아웃
// ---------------------------------------------------------------------------
function MubinsoQuestionScreen({
  title,
  options,
  value,
  onChange,
  onNext,
  onPrev,
  prevDisabled = false,
}: {
  title: string;
  options: string[];
  value: string;
  onChange: (answer: string) => void;
  onNext: () => void;
  onPrev: () => void;
  prevDisabled?: boolean;
}) {
  return (
    <BaseStack width="100%" maxW="480px" m="0 auto" minH="100dvh" px={16}>
      <Spacing gap={80} />
      <Box width="100%" whiteSpace="pre-line" className="head2_b black">
        {title}
      </Box>

      <Spacing gap={24} />
      <Box pt="24px" width="100%">
        <Field.ButtonGroups
          items={options}
          value={value}
          onChange={onChange}
          buttonWidth="100%"
          borderColor={colors.white}
        />
      </Box>

      <Spacing gap={24} />
      <BaseStack
        direction="horizontal"
        width="100%"
        alignItems="center"
        justifyContent="space-between"
        spacing={24}
        pt={4}
      >
        <Box width="100%">
          <PrevNext disabled={prevDisabled} text="이전" variant="filled" onClick={onPrev} />
        </Box>
        <Box width="100%">
          <PrevNext disabled={!value} text="다음" onClick={onNext} />
        </Box>
      </BaseStack>
      <Spacing gap={40} />
    </BaseStack>
  );
}
